import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { can, rupees } from "@/lib/helpers";
import { PageHeader, EmptyState } from "@/components/Common";
import { DateFilters } from "@/pages/Expenses";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";

const EMPTY = { condition: "Old", metal: "Gold", weight: "", amount: "", purchase_date: new Date().toISOString().slice(0, 10), purity: "", customer_name: "", customer_address: "", customer_phone: "", notes: "" };

function Pick({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#6B6B6B]">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-white border-[#E6E2D6]" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Field({ label, k, form, setForm, type = "text", className = "" }) {
  return (
    <div className={className}><Label className="text-xs text-[#6B6B6B]">{label}</Label><Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-white border-[#E6E2D6]" data-testid={`pf-${k.replace(/_/g, "-")}`} /></div>
  );
}

export default function Purchases() {
  const { user, activeBranch } = useAuth();
  const [d, setD] = useState({ rows: [], total_amount: 0, total_weight: 0, count: 0 });
  const [f, setF] = useState({ start: "", end: "", metal: "", condition: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && p.set(k, v));
    if (activeBranch) p.set("branch_id", activeBranch);
    api.get(`/purchases?${p}`).then(({ data }) => setD(data)).catch(() => {});
  }, [f, activeBranch]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!(Number(form.weight) > 0)) return toast.error("Weight is required");
    if (!(Number(form.amount) > 0)) return toast.error("Purchase amount is required");
    try {
      const { data } = await api.post("/purchases", { ...form, weight: Number(form.weight), amount: Number(form.amount), branch_id: activeBranch });
      toast.success(`Purchase ${data.purchase_no} recorded`); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this purchase record?")) return;
    try { await api.delete(`/purchases/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 lg:p-8 fade-in">
      <PageHeader title="Purchases" subtitle="New / old gold, silver and diamond bought by the shop.">
        {can(user, "purchases", "create") && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="add-purchase-btn"><Plus size={16} className="mr-1" /> New Purchase</Button>}
      </PageHeader>

      <DateFilters f={f} setF={setF}>
        <div className="w-36"><Pick label="Type" value={f.metal || "All"} onChange={(v) => setF({ ...f, metal: v === "All" ? "" : v })} options={["All", "Gold", "Silver", "Diamond"]} testid="filter-metal" /></div>
        <div className="w-32"><Pick label="New / Old" value={f.condition || "All"} onChange={(v) => setF({ ...f, condition: v === "All" ? "" : v })} options={["All", "New", "Old"]} testid="filter-condition" /></div>
      </DateFilters>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <Card className="p-4 bg-[#1A1A1A] text-white border-0" data-testid="purchase-total-amount"><div className="text-[10px] uppercase tracking-wider text-[#C5A059]">Total Purchase Amount</div><div className="text-xl font-medium tabular-nums mt-1">{rupees(d.total_amount)}</div></Card>
        <Card className="p-4 bg-white border-[#E6E2D6]" data-testid="purchase-total-weight"><div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">Total Weight</div><div className="text-xl font-medium tabular-nums mt-1">{Number(d.total_weight).toFixed(3)} g</div></Card>
        <Card className="p-4 bg-white border-[#E6E2D6]" data-testid="purchase-count"><div className="text-[10px] uppercase tracking-wider text-[#8A8A8A]">Purchases</div><div className="text-xl font-medium tabular-nums mt-1">{d.count}</div></Card>
      </div>

      <Card className="bg-white border-[#E6E2D6] shadow-sm overflow-x-auto">
        {d.rows.length === 0 ? <EmptyState label="No purchases recorded" /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No.</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Weight (g)</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Seller</TableHead><TableHead>Phone</TableHead><TableHead>By</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>{d.rows.map((r) => (
              <TableRow key={r.id} data-testid={`purchase-row-${r.purchase_no}`}>
                <TableCell className="font-medium">{r.purchase_no}</TableCell><TableCell className="text-sm">{r.purchase_date.slice(0, 10)}</TableCell>
                <TableCell><Badge className={`border-0 ${r.condition === "Old" ? "bg-[#FFF8E1] text-[#B8860B]" : "bg-[#E8F5E9] text-[#2D6A4F]"}`}>{r.condition}</Badge> <span className="text-sm">{r.metal}{r.purity ? ` ${r.purity}` : ""}</span></TableCell>
                <TableCell className="text-right tabular-nums">{Number(r.weight).toFixed(3)}</TableCell><TableCell className="text-right tabular-nums font-medium">{rupees(r.amount)}</TableCell>
                <TableCell className="text-sm">{r.customer_name || "-"}{r.customer_address && <div className="text-[11px] text-[#8A8A8A] max-w-[180px] truncate">{r.customer_address}</div>}</TableCell>
                <TableCell className="text-sm">{r.customer_phone || "-"}</TableCell><TableCell className="text-xs text-[#8A8A8A]">{r.created_by}</TableCell>
                <TableCell>{can(user, "purchases", "delete") && <Button size="icon" variant="ghost" className="text-[#9D2A2A]" onClick={() => remove(r.id)} data-testid={`purchase-delete-${r.purchase_no}`}><Trash size={15} /></Button>}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Purchase</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Pick label="New / Old *" value={form.condition} onChange={(v) => setForm({ ...form, condition: v })} options={["Old", "New"]} testid="pf-condition" />
            <Pick label="Type *" value={form.metal} onChange={(v) => setForm({ ...form, metal: v })} options={["Gold", "Silver", "Diamond"]} testid="pf-metal" />
            <Field label="Weight (g) *" k="weight" type="number" form={form} setForm={setForm} />
            <Field label="Purchase Amount ₹ *" k="amount" type="number" form={form} setForm={setForm} />
            <Field label="Date *" k="purchase_date" type="date" form={form} setForm={setForm} />
            <Field label="Purity (optional)" k="purity" form={form} setForm={setForm} />
            <div className="col-span-2 text-[11px] uppercase tracking-wider text-[#8A6D2F] mt-1">Seller details (optional)</div>
            <Field label="Customer Name" k="customer_name" form={form} setForm={setForm} />
            <Field label="Phone Number" k="customer_phone" form={form} setForm={setForm} />
            <Field label="Address" k="customer_address" form={form} setForm={setForm} className="col-span-2" />
            <Field label="Notes" k="notes" form={form} setForm={setForm} className="col-span-2" />
          </div>
          <DialogFooter><Button onClick={save} className="bg-[#C5A059] hover:bg-[#B38F4E] text-white" data-testid="pf-save">Save Purchase</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
